<script>
  import { modal } from '../stores/modalOpen.js';
  import { onMount } from 'svelte';
  import { panoramaxThumbUrl, panoramaxViewerUrl } from '../lib/panoramax.js';
  import { _ } from 'svelte-i18n';
  import MapCompleteLink from './MapCompleteLink.svelte';

  /** @type {{ uuids?: string[], mcUrl?: string }} */
  let { uuids: uuidsProp = [], mcUrl = '' } = $props();
  // Defensive: a parent passing `uuids={null}` would bypass the destructure
  // default (which only fires for undefined). The derived form keeps the
  // rest of the component free of `uuids?.length` checks.
  const uuids = $derived(uuidsProp ?? []);

  // Host lives in lib/panoramax.js, which documents why Panoramax is the one
  // service that is not proxied.
  const thumbUrl  = panoramaxThumbUrl;
  const viewerUrl = panoramaxViewerUrl;

  let fullscreen = $state(false);
  let modalIndex = $state(0);
  let selectedIndex = $state(0);
  let thumbFailed = $state(false);

  // The component instance is REUSED across playgrounds: PlaygroundPanel keeps
  // rendering it as long as the photos section is open, so selecting a
  // playground with one photo after one with three left selectedIndex at 2 and
  // uuids[2] undefined — a broken preview, a junk request for
  // /api/pictures/undefined/thumb.jpg, and a modal headed "3 / 1". Reset when
  // the photo set changes rather than clamping, so the visitor always starts
  // on the first photo of the playground they just selected.
  $effect(() => {
    uuids;                     // tracked: re-run whenever the photo set changes
    selectedIndex = 0;
    modalIndex = 0;
    thumbFailed = false;
  });

  function openModal(i) {
    modalIndex = i;
    fullscreen = true;
  }
  function closeModal() { fullscreen = false; }
  function prev() { modalIndex = (modalIndex - 1 + uuids.length) % uuids.length; }
  function next() { modalIndex = (modalIndex + 1) % uuids.length; }

  // Registered in capture phase so this fires before PlaygroundPanel's bubble-phase
  // ESC handler, preventing the panel from closing when only the photo modal should close.
  function onKeydown(e) {
    if (!fullscreen) return;
    if (e.key === 'ArrowLeft')  { e.preventDefault(); prev(); }
    if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    if (e.key === 'Escape')     { e.stopImmediatePropagation(); closeModal(); }
  }

  onMount(() => {
    window.addEventListener('keydown', onKeydown, { capture: true });
    return () => window.removeEventListener('keydown', onKeydown, { capture: true });
  });

  /**
   * Svelte action to portal an element to document.body.
   * This ensures the modal escapes the sidebar's stacking context
   * and works correctly in all browsers including Safari.
   */
  function portal(node) {
    document.body.appendChild(node);
    return {
      destroy() {
        if (node.parentNode) {
          node.parentNode.removeChild(node);
        }
      }
    };
  }
</script>

{#if uuids.length === 0}
  <div class="text-center py-2">
    <span class="bi bi-camera" style="font-size:1.8rem; color:#d1d5db;"></span>
    <p class="text-muted mt-1 mb-2" style="font-size:smaller;">{$_('panoramax.noPhotos')}</p>
    <MapCompleteLink href={mcUrl} label={$_('popup.addPhoto')} />
  </div>
{:else}
  <!-- Inline preview: the THUMBNAIL, not the viewer.
       This used to be an <iframe> loaded as soon as a playground with photos
       was selected. An iframe is not an image: it gets its own browsing
       context on the provider's origin, with cookies, localStorage and
       whatever script runs there — measured, the viewer attempts to set a
       Matomo `_pk_id` cookie. That is the strongest third-party capability on
       the page, and it was activating without the visitor asking to see a
       photo. The thumbnail is a plain image request, and the iframe is now
       created only by openModal(). -->
  <button type="button" class="panoramax-preview"
          onclick={() => openModal(selectedIndex)}
          aria-label={$_('panoramax.loadViewer')}
          title={$_('panoramax.loadViewerHint')}
  >
    {#if thumbFailed}
      <!-- A UUID can be present in OSM and gone upstream. Without this the
           visitor gets the browser's broken-image glyph under a play button
           whose label promises a photo; the old iframe at least rendered the
           provider's own error state. -->
      <div class="panoramax-thumb-missing">
        <span class="bi bi-camera" aria-hidden="true"></span>
      </div>
    {:else}
      <img
        src={thumbUrl(uuids[selectedIndex])}
        alt={$_('modal.streetPhoto')}
        onerror={() => thumbFailed = true}
        referrerpolicy="no-referrer"
        style="width:100%; height:240px; object-fit:cover; border:none; border-radius:4px; display:block;"
      />
    {/if}
    <div class="panoramax-overlay">
      <span class="bi bi-play-fill panoramax-play" aria-hidden="true"></span>
      <span class="bi bi-fullscreen panoramax-expand" aria-hidden="true"></span>
    </div>
  </button>

  <!-- Thumbnail strip for multiple photos -->
  {#if uuids.length > 1}
    <div class="d-flex gap-1 mt-1 flex-wrap">
      {#each uuids as uuid, i}
        <button type="button" class="thumb-btn {i === selectedIndex ? 'thumb-active' : ''}"
                onclick={() => selectedIndex = i}
                title={$_('photos.thumbnailTitle', { values: { n: i + 1, total: uuids.length } })}>
          <img src={thumbUrl(uuid)} alt={$_('photos.thumbnail', { values: { n: i + 1 } })}
               referrerpolicy="no-referrer"
               style="width:52px; height:36px; object-fit:cover; border-radius:3px;" />
        </button>
      {/each}
    </div>
  {/if}

  <p class="mt-1 mb-0">
    <MapCompleteLink href={mcUrl} label={$_('popup.addPhoto')} />
  </p>

{/if}

<!-- Fullscreen modal: portaled to document.body to escape sidebar stacking context -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
{#if fullscreen}
  <div use:portal class="panoramax-modal-backdrop" onclick={closeModal}>
    <div class="panoramax-modal" onclick={e => e.stopPropagation()}
         role="dialog" aria-modal="true" use:modal aria-label={$_('modal.streetPhoto')} tabindex="-1">
      <div class="panoramax-modal-header">
        <div class="panoramax-nav">
          <button type="button" class="nav-btn"
                  onclick={prev} disabled={uuids.length < 2} title={$_('modal.prevPhoto')}>
            &#8249;
          </button>
          <button type="button" class="nav-btn"
                  onclick={next} disabled={uuids.length < 2} title={$_('modal.nextPhoto')}>
            &#8250;
          </button>
          <span class="photo-counter">{modalIndex + 1} / {uuids.length}</span>
          <span class="photo-title">{$_('modal.streetPhoto')}</span>
        </div>
        <button type="button" class="close-btn" onclick={closeModal} aria-label={$_('modal.closeBtn')}>
          &#10005;
        </button>
      </div>
      <!-- The narrowest sandbox the viewer actually works under, established by
           probing the live viewer rather than assumed: with allow-scripts
           alone it renders nothing (no canvas), and adding allow-same-origin
           makes it identical to an unsandboxed frame. Combining those two is
           safe here specifically because the framed document is cross-origin —
           the usual warning applies when it is same-origin with the embedder,
           which this never is. -->
      <iframe
        src={viewerUrl(uuids[modalIndex])}
        style="width:100%; flex:1; border:none;"
        title={$_('photos.thumbnailTitle', { values: { n: modalIndex + 1, total: uuids.length } })}
        referrerpolicy="no-referrer"
        sandbox="allow-scripts allow-same-origin"
        allowfullscreen
      ></iframe>
    </div>
  </div>
{/if}

<style>
  /* A real <button> now, so the browser gives it keyboard activation and a
     focus ring for free; the reset below is what a button needs to look like
     the image container it replaced. */
  .panoramax-preview {
    position: relative;
    display: block;
    width: 100%;
    padding: 0;
    border: none;
    background: #f3f4f6;
    cursor: pointer;
    border-radius: 4px;
    overflow: hidden;
  }
  .panoramax-preview:focus-visible {
    outline: 2px solid #0d6efd;
    outline-offset: 2px;
  }
  .panoramax-overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
    border-radius: 4px;
  }
  /* Says "this will play something" rather than "this is a photo", so the
     visitor knows an activation is what loads the viewer. */
  .panoramax-play {
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    border-radius: 50%;
    width: 48px; height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    pointer-events: none;
  }
  .panoramax-preview:hover .panoramax-play {
    background: rgba(0, 0, 0, 0.72);
  }
  .panoramax-thumb-missing {
    height: 240px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
    color: #d1d5db;
    font-size: 2.2rem;
  }
  .panoramax-expand {
    position: absolute;
    bottom: 8px; right: 8px;
    background: rgba(255,255,255,0.85);
    border-radius: 3px;
    padding: 4px 5px;
    font-size: 13px;
    pointer-events: none;
  }

  .thumb-btn {
    background: none; border: 2px solid transparent; border-radius: 3px;
    padding: 0; cursor: pointer;
  }
  .thumb-active { border-color: #0d6efd; }

  .panoramax-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.75);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .panoramax-modal {
    background: #fff;
    border-radius: 8px;
    width: min(92vw, 1100px);
    height: min(85vh, 750px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    margin: auto;
    max-width: 1100px;
  }
  @media (max-width: 768px) {
    .panoramax-modal {
      border-radius: 0;
      width: 100vw;
      height: 100dvh;
      margin: 0;
      max-width: none;
    }
  }

  .panoramax-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
    flex-shrink: 0;
  }

  .panoramax-nav {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .nav-btn {
    background: #ffffff;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    line-height: 1;
    cursor: pointer;
    color: #374151;
    padding: 0;
  }
  .nav-btn:hover:not(:disabled) { background: #f3f4f6; }
  .nav-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .photo-counter {
    font-size: 0.8rem;
    color: #6b7280;
  }

  .photo-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #111827;
    margin-left: 0.25rem;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 1rem;
    color: #6b7280;
    cursor: pointer;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    line-height: 1;
  }
  .close-btn:hover { background: #f3f4f6; color: #111827; }

</style>
